import React, {useContext, useEffect, useRef, useState} from 'react';
import { useSelector, useDispatch } from 'react-redux'
import Box from "@mui/material/Box";
import {
    getAllClientsById,
    getClientsAllIds,
    getClientsStatus,
    getCustomerClients
} from "../../redux/CustomerClientsSlice";
import Grid from "@mui/material/Grid2";
import Button from "@mui/material/Button";
import AddIcon from '@mui/icons-material/Add';
import {
    Alert, AlertTitle, Collapse,
    InputAdornment, Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from "@mui/material";
import ProjectDetailRow from "../../Components/Projects/ProjectDetailRow";
import { ulid } from 'ulid'
import {
    getQuoteTemplates,
    getQuoteTemplatesStatus,
    postNewQuote,
    selectGetQuoteTemplatesById
} from "../../redux/ProjectsSlice";
import Typography from "@mui/material/Typography";
import ValidatedTextField from "../../Components/Inputs/ValidatedTextField";
import {alphaNumericCodeEmptyValidator, alphaNumericValidator, checkAllValid} from "../../Helpers/ValidationHelpers";
import AutocompleteValidatedTextField from "../../Components/Inputs/AutocompleteValidatedTextField";
import {
    getAllProductsStatus, getProducts,
    selectGetAllProductGroupsAllIds,
    selectGetAllProductGroupsById, selectGetAllProductItemsAllIds, selectGetAllProductItemsById
} from "../../redux/ProductsSlice";
import TrimObjectStrings from "../../Helpers/TrimObjectStrings";
import {IsOnboardedContext} from "../../Contexts/IsOnboardedContext";
import {useTheme} from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import LoadingWithStatusOverlay from "../../Components/Loading/LoadingWithStatusOverlay";
import {useNavigate} from "react-router";
import SaveIcon from "@mui/icons-material/Save";

function CreateProject() {
    useEffect(() => {
        document.title = 'New Project - CPC';
    }, []);
    const context = useContext(IsOnboardedContext);
    const theme = useTheme();
    const navigate = useNavigate();

    const [addProductWarning, setAddProductWarning] = useState(false);
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [quoteId, setQuoteId] = useState(null);
    const [invalidFormError, setInvalidFormError] = useState(false);
    const [allQuoteDetailsSaved, setAllQuoteDetailsSaved] = useState({});
    const [loadingStatus, setLoadingStatus] = useState("idle");
    const [loadingMessage, setLoadingMessage] = useState("");
    const dispatch = useDispatch();
    
    const customerClients = useSelector(getAllClientsById);
    const customerClientIds = useSelector(getClientsAllIds);
    const clientsStatus = useSelector(getClientsStatus);

    const productGroups = useSelector(selectGetAllProductGroupsById);
    const productGroupIds = useSelector(selectGetAllProductGroupsAllIds);

    const productItems = useSelector(selectGetAllProductItemsById);
    const productItemIds = useSelector(selectGetAllProductItemsAllIds)
    const productsStatus = useSelector(getAllProductsStatus);
    
    const quoteTemplates = useSelector(selectGetQuoteTemplatesById);
    const quoteTemplatesStatus = useSelector(getQuoteTemplatesStatus);
    
    const [activeForm, setActiveForm] = useState({
        clientId: '',
        createdBy: context?.userName,
        createdByEmail: context?.user?.email,
        jobNumber: null,
        externalQuoteNumber: null,
        description: '',
        quoteStatus: 1,
        customerCode: context?.orgDetails?.customerCode,
        quoteDetails: [],
    });
    
    const formValid = useRef({
        jobNumber: true,
        externalQuoteNumber: true,
        clientId: false,
        description: false
    });

    useEffect(() => {
        if (productsStatus === 'idle' || quoteTemplatesStatus === 'idle') {
            if (productsStatus === 'idle') {
                dispatch(getProducts());
            }
            dispatch(getQuoteTemplates()); // Ensure templates are updated when products reload
        }
    }, [dispatch, productsStatus, quoteTemplatesStatus]);

    useEffect(() => {
        if (clientsStatus === 'idle') {
            dispatch(getCustomerClients());
        }
    }, [dispatch, clientsStatus]);

    useEffect(() => {
       handleProductAddToQuote();
    }, []);

    function handleSubmit(event) {
        event.preventDefault();

        if (activeForm.quoteDetails.length === 0) {
            setAddProductWarning(true);
            return;
        }

        if (!checkAllValid(formValid.current)) {
            setInvalidFormError(true);
            return;
        }

        submitForm();
    }

    function submitForm() {
        setIsSubmitting(true);
        setInvalidFormError(false);
        setAddProductWarning(false);
        setLoadingStatus("loading");
        setLoadingMessage("Creating new project...");
        dispatch(postNewQuote(TrimObjectStrings(activeForm)))
            .unwrap()
            .then((response) => {
                setQuoteId(response.id);
                setIsSubmitting(false);
                setLoadingStatus("success");
                setLoadingMessage(`Project ${response.friendlyId} created successfully!`);
                setActiveForm({
                    clientId: '',
                    createdBy: context.userName,
                    description: '',
                    quoteStatus: 1,
                    customerCode: context.orgDetails.customerCode,
                    quoteDetails: [],
                });
            })
            .catch((error) => {
                console.error('Failed to post new quote', error);
                setIsSubmitting(false);
                const errorMessage = error?.status === 409
                        ? `Failed to create project. ${error?.detail}.`
                        : "Failed to create project. Please try again.";
                setLoadingStatus("error");
                setLoadingMessage(errorMessage);
            });
    }

    const handleProductAddToQuote = () => {
        const newId = ulid();
        const newDetail = {
            tempId: newId,
            quoteDetailFieldValues: []
        };
        setActiveForm(prevState => ({
            ...prevState,
            quoteDetails: [...prevState.quoteDetails, newDetail]
        }));
        
        setAllQuoteDetailsSaved(prevState => ({
            ...prevState,
            [newId]: false
        }));
    };

    const handleDetailChange = (tempId, updatedDetail) => {
        setActiveForm(prevQuote => {
            const updatedQuoteDetails = prevQuote.quoteDetails.map(detail =>
                detail.tempId === tempId
                    ? updatedDetail
                    : detail
            );

            return { ...prevQuote, quoteDetails: updatedQuoteDetails };
        });
    };

    const customerClientOptions = 
        customerClientIds?.map(customerClientId => ({
            key: customerClientId,
            name: customerClients[customerClientId]?.name,
        }));

    return (
        <div>
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    '& .MuiTextField-root': { width: '40ch' },
                    "& .MuiInputBase-input": {
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                    }
                }}>
                <Grid container spacing={2}>
                    <Grid size={12} sx={{ mb: 2 }}>
                        <Typography variant="h2">New Project</Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Box
                            display="flex"
                            justifyContent={"left"}
                        >
                            <ValidatedTextField
                                id="externalQuoteNumber"
                                label="Enter Quote Number"
                                size={"small"}
                                validator={alphaNumericCodeEmptyValidator}
                                onChange={isValid => (formValid.current.externalQuoteNumber = isValid)}
                                setActiveForm={setActiveForm}
                                isSubmitting={isSubmitting}
                                initialValue={""}
                                removeHelperTextPadding
                                fieldPath={['externalQuoteNumber']}
                                infoAdornment={<InputAdornment position="end">
                                    <Tooltip enterTouchDelay={200} disableFocusListener title={"If you have a quote number from an external system, enter it here."}>
                                        <InfoIcon />
                                    </Tooltip>
                                </InputAdornment>}
                                width={{xs: '100%', md: '400px !important'}}
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Box
                            display="flex"
                            justifyContent={"left"}
                        >
                            <ValidatedTextField
                                id="jobNumber"
                                label="Enter Job Number"
                                size={"small"}
                                validator={alphaNumericCodeEmptyValidator}
                                onChange={isValid => (formValid.current.jobNumber = isValid)}
                                setActiveForm={setActiveForm}
                                isSubmitting={isSubmitting}
                                initialValue={""}
                                removeHelperTextPadding
                                fieldPath={['jobNumber']}
                                infoAdornment={<InputAdornment position="end">
                                    <Tooltip enterTouchDelay={200} disableFocusListener title={"If you have a job number from an external system, enter it here."}>
                                        <InfoIcon />
                                    </Tooltip>
                                </InputAdornment>}
                                width={{xs: '100%', md: '400px !important'}}
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Box
                            display="flex"
                            justifyContent={"left"}
                        >
                            <ValidatedTextField
                                id="description"
                                label="Enter Description"
                                size={"small"}
                                validator={alphaNumericValidator}
                                onChange={isValid => (formValid.current.description = isValid)}
                                setActiveForm={setActiveForm}
                                isSubmitting={isSubmitting}
                                initialValue={""}
                                removeHelperTextPadding
                                fieldPath={['description']}
                                infoAdornment={null}
                                required
                                width={{xs: '100%', md: '400px !important'}}
                            />
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Box
                            display="flex"
                            justifyContent={"left"}
                        >
                            <AutocompleteValidatedTextField
                                isSubmitting={isSubmitting}
                                setActiveForm={setActiveForm}
                                label={"Select Client"}
                                size={"small"}
                                loadingStatus={clientsStatus}
                                options={customerClientOptions}
                                removeHelperTextPadding
                                onChange={isValid => (formValid.current.clientId = isValid)}
                                id={"clientId"}
                                initialValue={null}
                                infoAdornment={null}
                                required
                                width={{xs: '100%', md: '400px !important'}}
                            />
                        </Box>
                    </Grid>
                </Grid>
                <Grid container
                      spacing={2}
                >
                    <Grid size={{ xs: 12 }}>
                        <TableContainer>
                            <Collapse in={activeForm.quoteDetails.length > 0} >
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <Typography variant={"h4"}>Products</Typography>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {activeForm.quoteDetails.map((detail) => (
                                            <ProjectDetailRow
                                                key={detail.tempId ?? detail.id}
                                                quoteDetailId={detail.tempId}
                                                activeForm={activeForm}
                                                setActiveForm={setActiveForm}
                                                activeQuoteDetail={detail}
                                                handleDetailChange={handleDetailChange}
                                                productGroups={productGroups}
                                                productItems={productItems}
                                                quoteTemplates={quoteTemplates}
                                                quoteTemplatesStatus={quoteTemplatesStatus}
                                                productsStatus={productsStatus}
                                                productGroupIds={productGroupIds}
                                                productItemIds={productItemIds}
                                                isSubmitting={isSubmitting}
                                                handleProductAddToQuote={handleProductAddToQuote}
                                                allQuoteDetailsSaved={allQuoteDetailsSaved}
                                                setAllQuoteDetailsSaved={setAllQuoteDetailsSaved}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </Collapse>
                        </TableContainer>
                    </Grid>
                    <Grid size={{ xs: 12 }} sx={{my: 2}}>
                        <Button 
                            variant="contained" 
                            sx={{
                                backgroundColor: theme.palette.tertiary.secondary
                            }} 
                            startIcon={<AddIcon/>} 
                            onClick={handleProductAddToQuote}
                        >
                            New Product
                        </Button>
                    </Grid>
                    {addProductWarning && activeForm.quoteDetails.length === 0 && 
                        <Grid size={{ xs: 12 }}>
                            <Alert severity="warning" >
                                Please add at least one product to the quote.
                            </Alert>
                        </Grid>
                    }
                    {invalidFormError &&
                        <Grid size={{ xs: 12 }}>
                            <Alert severity="error" >
                                <AlertTitle>Validation Error</AlertTitle>
                                Please ensure all fields are correctly filled out.
                            </Alert>
                        </Grid>
                    }
                    <Grid size={{ xs: 12 }}>
                        <Button type="submit" 
                            disabled={isSubmitting || !checkAllValid(formValid.current) || !checkAllValid(allQuoteDetailsSaved)}
                            variant="contained"
                            startIcon={<SaveIcon/>}
                            color="primary"
                        >
                            Save
                        </Button>
                        <LoadingWithStatusOverlay
                            status={loadingStatus}
                            message={loadingMessage}
                            successTimeout={1500}
                            failureTimeout={5000}
                            onComplete={() => {
                                if (loadingStatus === "success") {
                                    navigate(`/projects/${quoteId}`);
                                }
                                setLoadingStatus("idle");
                                setLoadingMessage("");
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
        </div>
    )
}


export default CreateProject;